// Notification system

type NotificationType = 'info' | 'success' | 'warning' | 'error';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: NotificationType;
  timestamp: Date;
  read: boolean;
}

class NotificationService {
  private notifications: Notification[] = [];
  private listeners: ((notifications: Notification[]) => void)[] = [];

  addNotification(title: string, message: string, type: NotificationType = 'info') {
    const notification: Notification = {
      id: Math.random().toString(36).substr(2, 9),
      title,
      message,
      type,
      timestamp: new Date(),
      read: false
    };
    this.notifications.unshift(notification);
    this.notifyListeners();
    
    // Browser notification
    if (Notification.permission === 'granted') {
      new Notification(title, { body: message });
    }
  }

  requestPermission() {
    if ('Notification' in window) {
      Notification.requestPermission();
    }
  }

  getNotifications(): Notification[] {
    return this.notifications;
  }

  markAsRead(id: string) {
    const notif = this.notifications.find(n => n.id === id);
    if (notif) {
      notif.read = true;
      this.notifyListeners();
    }
  }

  markAllAsRead() {
    this.notifications.forEach(n => n.read = true);
    this.notifyListeners();
  }

  subscribe(listener: (notifications: Notification[]) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener([...this.notifications]));
  }
}

export const notificationService = new NotificationService();

// Pre-scheduled notifications
export const scheduleClassReminder = (classTime: Date, className: string) => {
  const now = new Date();
  const reminderTime = new Date(classTime.getTime() - 10 * 60 * 1000); // 10 min before
  
  if (reminderTime > now) {
    setTimeout(() => {
      notificationService.addNotification(
        'Class Starting Soon!',
        `${className} starts in 10 minutes`,
        'warning'
      );
    }, reminderTime.getTime() - now.getTime());
  }
};

export const sendWeeklyProgressReport = (studentName: string, progress: number) => {
  notificationService.addNotification(
    'Weekly Progress Report',
    `Hi ${studentName}! You've completed ${progress}% of this week's goals. Keep it up!`,
    'success'
  );
};
